const MARKDOWN_HELP = `
# String Manipulation (문자열 조작)

String Manipulation 기능을 사용하면 **텍스트 데이터를 다루는 다양한 작업**을 수행할 수 있습니다.  
이 기능은 **문자열 변환, 트리밍(Trim), 치환(Replace), 결합(Concatenation) 등**을 지원하며,  
데이터를 보다 효과적으로 처리하고 변환할 수 있습니다.

---

## 🛠 사용 방법

1. **입력값**: 조작할 텍스트 데이터 (예: 이름, 주소, 설명 등)
2. **조작 방식 선택**: 제공되는 다양한 문자열 조작 기능 중 선택
3. **출력값**: 변환된 문자열 반환

---

## 📌 지원되는 문자열 조작 기능

| 기능명         | 설명 |
|--------------|------|
| **Uppercase** | 모든 문자를 대문자로 변환 |
| **Lowercase** | 모든 문자를 소문자로 변환 |
| **Capitalize** | 첫 글자만 대문자로 변환 |
| **Trim** | 앞뒤 공백 제거 |
| **Replace** | 특정 문자열을 다른 문자열로 치환 |
| **Substring** | 문자열의 일부를 추출 |
| **Concat** | 여러 문자열을 연결 |
| **Pad Start** | 문자열을 지정된 길이만큼 앞에서 채우기 |
| **Pad End** | 문자열을 지정된 길이만큼 뒤에서 채우기 |
| **Split** | 문자열을 특정 구분자로 나누기 |
| **Join** | 배열로 나누어진 문자열을 특정 구분자로 결합 |

---

## 📌 예제 1: 대소문자 변환
### 입력값:
\`\`\`json
"hello world"
\`\`\`

### 변환 설정:
\`\`\`json
{
  "operation": "uppercase"
}
\`\`\`

### 결과:
\`\`\`json
"HELLO WORLD"
\`\`\`

---

## 📌 예제 2: 특정 문자열 치환
### 입력값:
\`\`\`json
"Welcome to AI"
\`\`\`

### 변환 설정:
\`\`\`json
{
  "operation": "replace",
  "find": "AI",
  "replaceWith": "ChatGPT"
}
\`\`\`

### 결과:
\`\`\`json
"Welcome to ChatGPT"
\`\`\`

---

## 📌 예제 3: 문자열 합치기
### 입력값:
\`\`\`json
["Hello", "World"]
\`\`\`

### 변환 설정:
\`\`\`json
{
  "operation": "concat",
  "separator": " "
}
\`\`\`

### 결과:
\`\`\`json
"Hello World"
\`\`\`

---

## 📌 예제 4: 문자열 자르기 (Substring)
### 입력값:
\`\`\`json
"abcdefg"
\`\`\`

### 변환 설정:
\`\`\`json
{
  "operation": "substring",
  "startIndex": 2,
  "length": 3
}
\`\`\`

### 결과:
\`\`\`json
"cde"
\`\`\`

---

## 📌 예제 5: 문자열 나누기 (Split)
### 입력값:
\`\`\`json
"apple,banana,grape"
\`\`\`

### 변환 설정:
\`\`\`json
{
  "operation": "split",
  "separator": ","
}
\`\`\`

### 결과:
\`\`\`json
["apple", "banana", "grape"]
\`\`\`

---

## ⚙ 설정 방법
1. **변환할 문자열을 입력값으로 제공**합니다.  
2. **적절한 문자열 조작 방식을 선택**합니다.  
3. **필요한 추가 설정(예: 치환 문자열, 패딩 길이 등)을 입력**합니다.  
4. **변환된 결과를 확인합니다.**

---

## 🔍 주요 특징
- **다양한 문자열 조작 기능 제공** (대소문자 변환, 치환, 결합 등)
- **입력값을 정제하고 가공하는 데 유용**
- **체인(Chaining) 방식으로 여러 변환을 연속 적용 가능**
- **데이터 정규화 및 표준화 작업에 적합**

💡 **String Manipulation을 사용하여 텍스트 데이터를 효율적으로 변환하세요!**
`

export const StringManipulation = {
  name: 'string-manipulation',
  label: 'String Manipulation',
  specifications: [
    {
      type: 'select',
      name: 'operation',
      label: 'Operation',
      property: {
        options: [
          { value: 'uppercase', display: 'Uppercase' },
          { value: 'lowercase', display: 'Lowercase' },
          { value: 'capitalize', display: 'Capitalize' },
          { value: 'trim', display: 'Trim' },
          { value: 'replace', display: 'Replace' },
          { value: 'substring', display: 'Substring' },
          { value: 'concat', display: 'Concatenate' },
          { value: 'padStart', display: 'Pad Start' },
          { value: 'padEnd', display: 'Pad End' },
          { value: 'split', display: 'Split' },
          { value: 'join', display: 'Join' }
        ]
      }
    },
    {
      type: 'text',
      name: 'find',
      label: 'Find (for Replace)',
      showIf: { operation: 'replace' }
    },
    {
      type: 'text',
      name: 'replaceWith',
      label: 'Replace With (for Replace)',
      showIf: { operation: 'replace' }
    },
    {
      type: 'number',
      name: 'startIndex',
      label: 'Start Index (for Substring)',
      showIf: { operation: 'substring' }
    },
    {
      type: 'number',
      name: 'length',
      label: 'Length (for Substring)',
      showIf: { operation: 'substring' }
    },
    {
      type: 'text',
      name: 'separator',
      label: 'Separator (for Split & Join)',
      showIf: { operation: ['split', 'join'] }
    }
  ],
  help: MARKDOWN_HELP
}
